import { Spinner } from '@/icons/loader/Spinner';
import React from 'react';

/**
 * App-wide button. Add a size or color here instead of creating a parallel
 * button component — overlay controls on media previews use size="icon" and
 * color="iconOverlay".
 */
const Button = React.forwardRef(({
  value = '',
  size = 'small',
  color = 'primary',
  border = 'medium',
  transition = 'ease',
  className = '',
  hoverTransiton = true,
  cursor = 'pointer',
  textSize = 'md',
  ...props
}, ref) => {
  const sizeClasses = {
    smallLong: 'h-[40px] p-[16px] font-medium leading-[24px]',
    small: 'h-[40px] font-medium leading-[24px] lg:w-fit',
    medium: 'h-[40px] pl-[20px] pr-[24px] py-[8px] font-medium leading-[24px]',
    large: 'h-[40px] font-medium leading-[24px]',
    // Square hit target for a lone icon (edit/delete chips on a preview).
    icon: 'h-8 w-8 shrink-0 p-0',
  };

  const textSizeClasses = {
    sm: "text-[14px]",
    md: "text-[16px]"
  }

  const cursorClasses = {
    pointer: 'cursor-pointer',
    default: 'cursor-default'
  }

  const transitionTypes = {
    ease: 'transition duration-200',
    leftRight: ''
  }

  const colorClasses = {
    primary: `bg-primary text-primaryforeground ${hoverTransiton ? `${transitionTypes[transition]} hover:bg-hoverprimary` : ''}`,
    secondary: `foreground border border-border bg-background ${hoverTransiton ? `${transitionTypes[transition]} hover:bg-input` : ''}`,
    destructive: `foreground border text-primaryforeground border-border bg-destructive ${hoverTransiton ? `${transitionTypes[transition]} hover:bg-destructivehover` : ''}`,
    invisibleGray: `bg-transparent border-border text-primaryforeground ${hoverTransiton ? `${transitionTypes[transition]} hover:bg-border` : ''}`,
    // Raised chip on top of images; tokens only so it follows theme / dark mode.
    // !ring so the focus ring wins over Button's shared `focus:ring-0` without editing that base class.
    // `ring-focus` (not `ring-ring`): --ring exists in CSS but is not a Tailwind color; `focus` is.
    iconOverlay: `border border-border bg-background text-foreground shadow-sm ${hoverTransiton ? `${transitionTypes[transition]} hover:bg-input` : ''} focus-visible:!ring-2 focus-visible:!ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background`,
  };

  const borderRadius = {
    medium: 'rounded-[12px]',
    full: 'rounded-full'
  }

  const isSpinner = React.isValidElement(value) && value.type === Spinner;
  if (isSpinner) cursor = 'default';


  return (
    <button
      ref={ref}
      className={`flex items-center justify-center focus:outline-none focus:ring-0 focus:ring-offset-2 ${sizeClasses[size] || ""} ${colorClasses[color] || ""} ${borderRadius[border] || ""} ${textSizeClasses[textSize] || textSize} ${className} ${cursorClasses[cursor]}`}
      {...props}
    >
      {value}
    </button>
  );
});

export { Button };
